import { distanceInKm } from './geo';
import { getPositionCityId } from './effectiveEndPosition';

// KD-3534 — a hotel this close to the day's arrival point is where the traveller
// is meant to sleep, however its city or region happens to be spelled. Tunable
// per environment, since the right radius differs between a dense city and a
// destination where the next hotel is half an hour away.
const DEFAULT_RADIUS_KM = 5;

// CRA inlines process.env.REACT_APP_* at build time, here as a string.
const parseRadiusKm = (value) => {
  const radiusKm = Number.parseFloat(value);

  return Number.isFinite(radiusKm) && radiusKm >= 0
    ? radiusKm
    : DEFAULT_RADIUS_KM;
};

export const HOTEL_LOCATION_CONFLICT_RADIUS_KM = parseRadiusKm(
  process.env.REACT_APP_HOTEL_LOCATION_CONFLICT_RADIUS_KM,
);

// Locations reach the front either as plain strings (hotel availability payload)
// or as { id, title } objects (hotel detail payload).
const locationName = (location) => {
  const name = typeof location === 'string' ? location : location?.title;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
};

const normalizeName = (name) =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// City ids arrive raw on positions and on the availability payload, base64
// gid-encoded on the hotel detail. Same decode as the apps' decodeId, kept
// local because packages must not import app utils.
const decodeCityId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number.parseInt(value, 10);
  if (!Number.isNaN(numeric)) {
    return String(numeric);
  }
  try {
    const decoded = atob(String(value).replace(/\n/g, ''));
    const tail = Number.parseInt(
      decoded.substring(decoded.lastIndexOf('/') + 1),
      10,
    );
    return Number.isNaN(tail) ? null : String(tail);
  } catch {
    return null;
  }
};

const isSuggestedByProgram = (hotel, program) => {
  const hotelId = hotel?.id?.toString();

  if (!hotelId) {
    return false;
  }

  return Boolean(
    program?.accomodationHotel?.id === hotelId ||
    program?.alternativeHotels?.some(({ id }) => id === hotelId),
  );
};

// Tells whether the hotel picked for a day sits away from where the day ends,
// which the Trip Designer surfaces as a "Conflict" badge and an error toast.
// `endPosition` is the day's EFFECTIVE end (KD-3548: a required transfer moves
// it); callers that have none fall back to the program's own location.
export const hasHotelLocationConflict = ({
  hotel,
  program,
  endPosition,
  radiusKm = HOTEL_LOCATION_CONFLICT_RADIUS_KM,
}) => {
  if (!hotel || !program) {
    return false;
  }

  // A hotel the program itself suggests is never a conflict, whatever the distance.
  if (isSuggestedByProgram(hotel, program)) {
    return false;
  }

  const dayEndPosition = endPosition ?? program?.endPosition;

  // KD-3635 — same referential city = same place, whatever the distance to the
  // position's point and however the two sides spell the name. The id is the
  // only comparison that survives "Hô Chi Minh" vs "Ho Chi Minh City".
  const hotelCityId = decodeCityId(hotel?.cityId ?? hotel?.city?.id);
  const dayCityId = decodeCityId(getPositionCityId(dayEndPosition));

  if (hotelCityId && dayCityId && hotelCityId === dayCityId) {
    return false;
  }

  // KD-3534 — the hotel's own city is what compares to the program's city. Its
  // region is only a fallback for payloads carrying no city (availability list),
  // where comparing a region to a city used to flag "Marrakech-Safi" against
  // "Marrakech" as a conflict.
  const hotelName = locationName(hotel?.city) ?? locationName(hotel?.region);
  const programName = locationName(dayEndPosition?.city);
  const hasMatchingNames =
    Boolean(hotelName && programName) &&
    normalizeName(hotelName) === normalizeName(programName);

  if (hasMatchingNames) {
    return false;
  }

  const distance = distanceInKm(hotel, dayEndPosition);

  if (distance !== null) {
    return distance > radiusKm;
  }

  // No coordinates on either side: fall back to comparing the location names.
  return Boolean(hotelName && programName);
};
